import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Scissors, Trash2, Undo2 } from "lucide-react";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { acquirePdfDocument } from "../utils/pdf-blob-cache";

import { tagNameToColor } from "@/lib/miscellaneous/tags";
import type { EditablePage } from "../types";
import {
  fetchAndCacheBlobUrl,
  getCachedBlobUrl,
} from "../utils/pdf-blob-cache";

function usePdfBlobUrl(url: string) {
  const isObjectUrl = url.startsWith("blob:");
  const [blobUrl, setBlobUrl] = useState<string | undefined>(
    isObjectUrl ? url : getCachedBlobUrl(url),
  );

  useEffect(() => {
    let mounted = true;
    if (isObjectUrl || blobUrl) return;

    fetchAndCacheBlobUrl(url)
      .then((u) => {
        if (!mounted) return;
        setBlobUrl(u);
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, [url, isObjectUrl, blobUrl]);

  return isObjectUrl ? url : getCachedBlobUrl(url) ?? blobUrl;
}

const MAX_THUMBS = 80;
const thumbCache = new Map<string, string>();
function getThumb(key: string) {
  return thumbCache.get(key);
}
function setThumb(key: string, url: string) {
  if (thumbCache.has(key)) thumbCache.delete(key);
  thumbCache.set(key, url);
  if (thumbCache.size > MAX_THUMBS) {
    const [evictKey, evictUrl] = thumbCache.entries().next().value as [
      string,
      string,
    ];
    thumbCache.delete(evictKey);
    URL.revokeObjectURL(evictUrl);
  }
}

export const SortablePageContainer = forwardRef<
  HTMLDivElement,
  PageContainerProps
>((props, ref) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.page.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <PageContainer
        {...props}
        ref={ref}
        dragHandleProps={listeners}
        isDragging={isDragging}
      />
    </div>
  );
});

type PageContainerProps = {
  page: EditablePage;
  pageIndex: number;
  focused: boolean;
  splits: number[];
  onSplit: (pageIndex: number) => void;
  onSelect: (pageIndex: number) => void;
  onDelete: (pageIndex: number) => void;
  totalPages: number;
};

const PageContainer = forwardRef<
  HTMLDivElement,
  PageContainerProps & {
    dragHandleProps?: any;
    isDragging?: boolean;
  }
>(
  (
    {
      page,
      pageIndex,
      focused,
      splits,
      onSelect,
      onDelete,
      onSplit,
      totalPages,
      dragHandleProps,
      isDragging,
    },
    ref,
  ) => {
    const [containerDimensions, setContainerDimensions] = useState({
      width: 0,
      height: 0,
    });

    const containerRef = useRef<HTMLDivElement>(null);

    const blobUrl = usePdfBlobUrl(page.sourcePdfUrl);

    const selected = useMemo(() => {
      return splits.includes(pageIndex);
    }, [splits, pageIndex]);

    const isFirstInSplit = useMemo(() => {
      return splits.includes(pageIndex - 1) || pageIndex === 0;
    }, [splits, pageIndex]);

    const isLastInSplit = useMemo(() => {
      return splits.includes(pageIndex) || pageIndex === totalPages - 1;
    }, [splits, pageIndex, totalPages]);

    useEffect(() => {
      const updateDimensions = () => {
        if (containerRef.current) {
          setContainerDimensions({
            width: containerRef.current.clientWidth,
            height: containerRef.current.clientHeight,
          });
        }
      };

      updateDimensions();
      window.addEventListener("resize", updateDimensions);

      return () => window.removeEventListener("resize", updateDimensions);
    }, []);

    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            setIsInView(entry.isIntersecting);
          }
        },
        { root: null, threshold: 0.01, rootMargin: "200px 0px" }, // smaller prefetch window
      );
      observer.observe(el);
      return () => observer.disconnect();
    }, []);

    const handleSplit = () => {
      onSplit(pageIndex);
    };

    const handleSelect = () => {
      onSelect(pageIndex);
    };

    const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      onDelete(pageIndex);
    };

    const colors = useMemo(() => {
      const color = tagNameToColor(page.sourceInvoiceFileName);

      return {
        backgroundColor: color.backgroundColor,
        color: color.textColor,
      };
    }, [page.sourceInvoiceFileName]);

    return (
      <div ref={ref} className="flex flex-row h-full">
        <div
          style={{
            boxSizing: "border-box",
          }}
          className={cn(
            "relative flex-1 border-gray-100 border-2 ",
            splits.length > 0 &&
              "bg-blue-200/80 border-blue-300 border-r-transparent border-l-transparent",
            splits.length > 0 &&
              isFirstInSplit &&
              "rounded-l-md border-l-blue-300",
            splits.length > 0 &&
              isLastInSplit &&
              "rounded-r-md border-r-blue-300",
            page.markedForDeletion && "opacity-50 grayscale",
            isDragging && "rotate-2 scale-105 shadow-lg z-50",
          )}
        >
          <div
            {...dragHandleProps}
            className={cn(
              "m-2 rounded-md",
              "cursor-grab active:cursor-grabbing",
              focused && "!outline !outline-2 !outline-blue-400 !bg-blue-50",
              "hover:outline-gray-300 hover:outline-dotted hover:bg-gray-50",
            )}
          >
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
            <div
              className="relative flex flex-col gap-4 p-2 items-center group"
              onClick={handleSelect}
              role="button"
            >
              <div className="absolute top-3 left-3 z-10">
                <div className="p-1.5 bg-white/90 backdrop-blur-sm rounded-md shadow-sm border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity">
                  <GripVertical className="h-4 w-4 text-gray-500" />
                </div>
              </div>
              <div className="absolute top-3 right-3 z-10">
                <Button
                  variant="destructive"
                  size="sm"
                  className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                  onClick={handleDelete}
                >
                  {page.markedForDeletion ? (
                    <Undo2 className="w-4 h-4" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <div
                ref={containerRef}
                className={cn(
                  "relative w-full aspect-[1/1.4142] max-h-[260px] overflow-hidden border border-gray-300 bg-white",
                )}
              >
                <ThumbnailImage
                  thumbnailUrl={page.thumbnailUrl}
                  pdfUrl={blobUrl}
                  sourcePageIndex={page.sourcePageIndex}
                  isInView={isInView}
                  containerWidth={containerDimensions.width}
                />
              </div>
              <div className="text-xs flex flex-col items-center gap-1 w-32">
                <Badge
                  variant="outline"
                  className="max-w-full"
                  style={colors}
                  title={page.sourceInvoiceFileName}
                >
                  <div
                    className={cn(
                      "truncate-short",
                      page.markedForDeletion && "line-through",
                    )}
                  >
                    {page.sourceInvoiceFileName}
                  </div>
                </Badge>
                <div
                  style={colors}
                  className={cn(
                    "mt-2 rounded-full w-6 h-6 flex items-center justify-center border border-gray-200 text-xs shadow-sm",
                    page.markedForDeletion && "line-through",
                  )}
                >
                  {pageIndex + 1}
                </div>
              </div>
            </div>
          </div>
        </div>
        {pageIndex < totalPages - 1 ? (
          <div
            style={{
              boxSizing: "border-box",
            }}
            className={cn(
              "z-10 h-full border-transparent border-t-2 border-b-2 border-l-0 border-r-0 mx-[-2px] px-[2px]",
              splits.length > 0 &&
                "border-t-blue-300 border-b-blue-300 border-l-0 border-r-0",
              selected &&
                "bg-transparent border-t-gray-100 border-b-gray-100 border-l-0 border-r-0",
            )}
          >
            <div
              className={cn(
                "h-full w-10",
                splits.length > 0 &&
                  "bg-blue-200/80 border-t-blue-300 border-b-blue-300 border-l-0 border-r-0",
              )}
            >
              <SplitAction
                onAction={handleSplit}
                selected={selected}
                isDragging={isDragging}
              />
            </div>
          </div>
        ) : (
          <div className="h-full w-10" />
        )}
      </div>
    );
  },
);

type ThumbnailImageProps = {
  thumbnailUrl?: string;
  sourcePageIndex: number;
  isInView: boolean;
  pdfUrl?: string;
  containerWidth: number;
};

function ThumbnailImage({
  thumbnailUrl,
  sourcePageIndex,
  isInView,
  pdfUrl,
  containerWidth,
}: ThumbnailImageProps) {
  const [localUrl, setLocalUrl] = useState<string | undefined>(undefined);
  const [imgReady, setImgReady] = useState(false);
  const prevUrlRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!isInView || !pdfUrl || !containerWidth) return;

    // account for DPR and container size to avoid reusing low-res thumbs
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cssWidth = Math.min(containerWidth, 400); // visual width cap
    const pixelWidth = Math.max(80, Math.min(Math.round(cssWidth * dpr), 800)); // safe pixel cap

    const key = `${pdfUrl}#${sourcePageIndex}#w${pixelWidth}`;
    const cached = getThumb(key);
    if (cached) {
      setLocalUrl(cached);
      return;
    }

    let cancelled = false;
    let renderTask: any | null = null;

    const prevUrl = localUrl;

    (async () => {
      const { promise, release } = acquirePdfDocument(pdfUrl!);

      try {
        const pdf = await promise;
        const page = await pdf.getPage(sourcePageIndex + 1);

        const baseViewport = page.getViewport({ scale: 1 });
        // render at device pixels for crispness; pdf.js will rasterize accordingly
        const scale = Math.max(0.1, pixelWidth / baseViewport.width);
        const viewport = page.getViewport({ scale });

        const useOffscreen = typeof OffscreenCanvas !== "undefined";
        let blob: Blob;

        if (useOffscreen) {
          const oc = new OffscreenCanvas(
            Math.floor(viewport.width),
            Math.floor(viewport.height),
          );
          const octx = oc.getContext("2d", {
            alpha: false,
          }) as OffscreenCanvasRenderingContext2D | null;
          if (!octx) {
            release();
            return;
          }
          renderTask = page.render({ canvasContext: octx as any, viewport });
          await renderTask.promise;
          //@ts-ignore
          blob = await oc.convertToBlob({ type: "image/jpeg", quality: 0.9 });
        } else {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d", { alpha: false });
          if (!ctx) {
            release();
            return;
          }
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          renderTask = page.render({ canvasContext: ctx, viewport });
          await renderTask.promise;
          blob = await new Promise<Blob>((resolve, reject) =>
            canvas.toBlob(
              (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
              "image/jpeg",
              0.9,
            ),
          );
          // help GC
          canvas.width = 0;
          canvas.height = 0;
        }

        const objectUrl = URL.createObjectURL(blob);

        if (!cancelled) {
          prevUrlRef.current = prevUrl;
          setThumb(key, objectUrl);
          setLocalUrl(objectUrl);

          // revoke after next paint to avoid transient broken image
          requestAnimationFrame(() => {
            const p = prevUrlRef.current;
            if (p && p !== objectUrl) URL.revokeObjectURL(p);
            prevUrlRef.current = undefined;
          });
        }

        renderTask = null;
        page.cleanup();
        release();
      } catch {
        // swallow
      }
    })();

    return () => {
      cancelled = true;
      try {
        renderTask?.cancel?.();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView, pdfUrl, containerWidth, sourcePageIndex]);

  const src = thumbnailUrl ?? localUrl;

  useEffect(() => {
    setImgReady(false);
  }, [src]);

  if (src && isInView) {
    return (
      <img
        key={src}
        src={src}
        alt={`Seite ${sourcePageIndex + 1}`}
        loading="lazy"
        decoding="async"
        draggable={false}
        onLoad={() => setImgReady(true)}
        onError={() => setImgReady(false)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          visibility: imgReady ? "visible" : "hidden",
        }}
        className="bg-gray-100"
      />
    );
  }

  return (
    <div className="absolute inset-0">
      <div className="w-full h-full bg-gray-100" />
    </div>
  );
}

export const SplitAction = ({
  selected = false,
  onAction,
  isDragging,
}: {
  selected?: boolean;
  onAction: () => void;
  isDragging?: boolean;
}) => {
  return (
    <div
      className={cn(
        "h-full flex justify-center items-center py-1",
        selected ? "bg-gray-100" : "",
        isDragging && "rotate-2",
      )}
    >
      <Button
        variant="ghost"
        className={cn(
          "group p-0 w-10 h-full relative text-muted-foreground hover:text-primary hover:border-text-primary hover:bg-transparent",
          selected && "bg-gray-100",
        )}
        onClick={onAction}
      >
        <div
          className={cn(
            "border-dashed border-l border-gray-400 h-full group-hover:border-primary group-hover:border-l-2",
            selected && "border-solid border-primary border-l-2",
          )}
        />
        <div
          className={cn(
            "flex items-center justify-center rounded-full border border-gray-400 bg-background absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-1 group-hover:border-primary group-hover:border-2",
            selected && "border-primary border-2 text-primary",
          )}
        >
          <Scissors className="w-4 h-4" />
        </div>
      </Button>
    </div>
  );
};
